import React, { useState } from "react";
import { LinkedBookingAction } from "../../../../../APIs/Booking/UpdateLinkedBookingsStatus";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "../../../../ui/dialog";
import { EDSButton } from "../../../../eds/EDSButton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "../../../../ui/select";

interface BulkActionOption {
  value: LinkedBookingAction;
  label: string;
}

export const BULK_ACTIONS: BulkActionOption[] = [
  { value: "confirm", label: "Confirm bookings" },
  { value: "reopen", label: "Reopen bookings" },
  { value: "reject", label: "Reject bookings" },
  { value: "take", label: "Take bookings" }
];

interface BulkStatusActionsProps {
  onExecute: (action: LinkedBookingAction) => void;
  isExecuting: boolean;
  disabled: boolean;
  bookingCount: number;
}

const BulkStatusActions = ({
  onExecute,
  isExecuting,
  disabled,
  bookingCount
}: BulkStatusActionsProps) => {
  const [selectedAction, setSelectedAction] = useState<
    LinkedBookingAction | ""
  >("");
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);

  const selectedOption = BULK_ACTIONS.find((a) => a.value === selectedAction);

  const handleGo = () => {
    if (!selectedOption) return;
    setIsConfirmDialogOpen(true);
  };

  const handleConfirm = () => {
    if (!selectedOption) return;
    onExecute(selectedOption.value);
    setIsConfirmDialogOpen(false);
    setSelectedAction("");
  };

  return (
    <div className="flex items-center gap-2">
      <Select
        value={selectedAction}
        onValueChange={(value: string) =>
          setSelectedAction(value as LinkedBookingAction)
        }
        disabled={disabled || isExecuting}
      >
        <SelectTrigger
          className="w-[140px] h-9"
          aria-label="Bulk status action"
        >
          <SelectValue placeholder="Select action" />
        </SelectTrigger>
        <SelectContent>
          {BULK_ACTIONS.map((action) => (
            <SelectItem key={action.value} value={action.value}>
              {action.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <EDSButton
        size="sm"
        onClick={handleGo}
        disabled={!selectedAction || disabled || isExecuting}
        aria-busy={isExecuting}
      >
        {isExecuting ? "Updating…" : "Go"}
      </EDSButton>

      <Dialog open={isConfirmDialogOpen} onOpenChange={setIsConfirmDialogOpen}>
        <DialogContent
          showCloseButton={false}
          aria-describedby="bulk-status-dialog-description"
          className="bg-[var(--panelBackground)] shadow-[0_3px_16px_0_rgba(0,0,0,0.16)] max-w-[400px] w-[90%]"
        >
          <DialogHeader>
            <DialogTitle>
              {selectedOption?.label} all linked bookings
            </DialogTitle>
            <DialogDescription id="bulk-status-dialog-description">
              Are you sure you want to {selectedOption?.label.toLowerCase()} all{" "}
              {bookingCount} linked bookings?
            </DialogDescription>
          </DialogHeader>

          <DialogFooter>
            <EDSButton
              variant="outline"
              size="md"
              onClick={() => setIsConfirmDialogOpen(false)}
              disabled={isExecuting}
            >
              Cancel
            </EDSButton>
            <EDSButton
              size="md"
              onClick={handleConfirm}
              disabled={isExecuting}
              aria-busy={isExecuting}
            >
              {isExecuting ? "Updating…" : (selectedOption?.label ?? "Confirm")}
            </EDSButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BulkStatusActions;
